import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { imagePrefix, getUserId, shopid } from "../../utils/url";
import productImage from "../../assets/productImage.png";
import { FaHeart, FaRegHeart } from "react-icons/fa";
import { AiOutlineLoading3Quarters } from "react-icons/ai";
import {
  addToWishlistController,
  removeFromWishlistController,
  getWishlistController,
} from "../../../node_modules/shops-query/src/modules/wishlist";
import {
  calculateDiscountPercentage,
  calculateFinalPrice,
} from "../../utils/priceCalculator";
import { useToast } from "../toast";
import { useContext } from "react";
import LoginModalContext from "../../contexts/LoginModalContext";

const ProductCard = ({ item, tag = "", showHeart = true, isThisWishlistPage = true, onRemove, id }) => {
  const navigate = useNavigate();
  const userId = getUserId();
  const [isWishlisted, setIsWishlisted] = useState(false);
  const [isLoadingWishlist, setIsLoadingWishlist] = useState(false);
  const { showToast, ToastContainer } = useToast();
  const { openLogin } = useContext(LoginModalContext);

  const image = item?.featureImage;
  const [activeImage, setActiveImage] = useState(image);

  useEffect(() => {
    setActiveImage(item?.featureImage);
  }, [item?.featureImage]);

  const discount = item?.discount || 0;
  const prize = item?.prize || 0;
  const finalPrice = calculateFinalPrice(prize, discount);
  const discountPercentage = calculateDiscountPercentage(prize, discount);

  const addId = Number(item?.id); // ID used when adding to wishlist
  const removeId = Number(item?.id); // ID used when removing from wishlist

  //  Check if product already in wishlist on mount
  useEffect(() => {
    if (isThisWishlistPage) {
      setIsWishlisted(true);
      return; // no need to fetch wishlist again
    }
    const fetchWishlist = async () => {
      try {
        const wishlistData = await getWishlistController(userId, shopid);

        const exists = wishlistData?.some(
          (p) => Number(p?.productId) === Number(item?.id || item?.productId)
        );
        setIsWishlisted(exists);
      } catch (error) {
        console.log("Error checking wishlist:", error);
      }
    };
    fetchWishlist();
  }, [item?.id, userId]);

  // Listen for global wishlist updates from other components
  useEffect(() => {
    const handleWishlistUpdate = (event) => {
      const { productId, isWishlisted: wishlistState } = event.detail;
      const currentProductId = Number(item?.id || item?.productId);
      if (Number(productId) === currentProductId) {
        setIsWishlisted(wishlistState);
      }
    };

    window.addEventListener("wishlistUpdated", handleWishlistUpdate);
    return () => window.removeEventListener("wishlistUpdated", handleWishlistUpdate);
  }, [item?.id, item?.productId]);

  // To remove from wishlist inside wishlist page
  const removeFromWishListPage = async (e) => {
    e.stopPropagation();
    setIsLoadingWishlist(true);
    try {
      setIsWishlisted(false);
      if (onRemove) onRemove(item?.productId);
      showToast({
        message: "Removed from wishlist",
        type: "success",
      });
      // Broadcast update to other components
      window.dispatchEvent(new CustomEvent("wishlistUpdated", {
        detail: { productId: item?.productId, isWishlisted: false }
      }));
    } finally {
      setIsLoadingWishlist(false);
    }
  };

  // To add and remove from outside wishlist page
  const handleWishlistToggle = async (e) => {
    e.stopPropagation();
    if (userId === 0 || userId === null) {
      openLogin();
      return;
    }

    setIsLoadingWishlist(true);
    try {
      const productId = Number(item?.id);

      if (isWishlisted) {
        await removeFromWishlistController({
          userId,
          productId,
          shopId: shopid,
        });
        setIsWishlisted(false);
        showToast({
          message: "Removed from wishlist",
          type: "success",
        });
      } else {
        await addToWishlistController(productId, shopid, userId);
        setIsWishlisted(true);
        showToast({
          message: "❤️ Product Added to wishlist",
          type: "success",
        });
      }
      window.dispatchEvent(new CustomEvent("wishlistUpdated", {
        detail: { productId, isWishlisted: !isWishlisted }
      }));
    } catch (error) {
      console.log("Wishlist operation failed:", error);
      setIsWishlisted(!isWishlisted);
      showToast({
        message: "Removed from wishlist",
        type: "error",
      });
    } finally {
      setIsLoadingWishlist(false);
    }
  };

  return (
    <div
      className="cursor-pointer group w-full"
      onClick={() => {
        // Use productId if from wishlist, otherwise use id
        const navigationId = id || item?.id || item?.productId;
        navigate(`/product/${navigationId}`);
      }}
    >
      {/* Image */}
      <div 
        className="relative overflow-hidden w-full md:h-[447px] h-[250px]"
        onMouseEnter={() => {
           if (item?.productImage && item.productImage.length > 0) {
             setActiveImage(item.productImage[0].image);
           }
        }}
        onMouseLeave={() => setActiveImage(image)}
      >
        <img
          src={activeImage ? imagePrefix + activeImage : productImage}
          alt={item?.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
        />
        {item?.productImage && item.productImage.length > 0 && (
          <img
            src={imagePrefix + item.productImage[0].image}
            alt={item?.title}
            className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-in-out group-hover:scale-105 ${
              activeImage === item.productImage[0].image ? "opacity-100" : "opacity-0"
            }`}
          />
        )}

        {tag && (
          <span className="absolute top-2 left-2 bg-[#262626] text-white text-[10px] sm:text-[12px] font-semibold px-2 py-1">
            {tag}
          </span>
        )}

        {showHeart && (
          <div
            onClick={
              isThisWishlistPage ? removeFromWishListPage : handleWishlistToggle
            }
            className="absolute top-3 right-3 text-white p-2 rounded-full text-[20px] sm:text-[24px]"
          >
            {isLoadingWishlist ? (
              <AiOutlineLoading3Quarters className="text-primary-default animate-spin" />
            ) : isWishlisted ? (
              <FaHeart className="text-primary-default" />
            ) : (
              <FaRegHeart className="text-black" />
            )}
          </div>
        )}
      </div>

      {/* Product Info */}
      <div className="mt-3">
        <h3 className="font-semibold text-[14px] sm:text-[16px] md:text-[18px] capitalize truncate">
          {item?.name || item?.title}
        </h3>

        {discount > 0 ? (
          <div className="flex flex-wrap items-center gap-2 mt-1">
            <p className="font-semibold text-[14px] sm:text-[16px] md:text-[20px] text-black">
              ₹{finalPrice}
            </p>
            <p className="text-[12px] sm:text-[14px] md:text-[18px] line-through text-[#C4C4C4]">
              ₹{Math.round(prize)}
            </p>
            <p className="font-semibold text-[12px] sm:text-[14px] md:text-[18px] text-primary-default">
              {Math.round(discountPercentage)}% OFF
            </p>
          </div>
        ) : (
          <p className="font-semibold text-[14px] sm:text-[16px] md:text-[20px] text-black mt-1">
            ₹{Math.round(prize)}
          </p>
        )}
      </div>
    </div>
  );
};

export default ProductCard;